import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MoreHorizontal, Plus } from 'lucide-react'
import type { GoalResponse } from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatDate, formatMonthYearShort } from '@/lib/formatDate'
import { DEFAULT_GOAL_ICON } from '@/lib/goalIconOptions'
import { paceBadge, timeGoneFraction, trackedIn } from '@/lib/goalPace'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MenuSurface } from '@/components/ui/menu-surface'
import { ICON_TILE } from '@/components/budget/MonthlyBudgets'

export interface GoalMenuItem {
  label: string
  danger?: boolean
  onSelect: () => void
}

function RowMenu({ name, items }: { name: string; items: GoalMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function close(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`More for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-[var(--fl-text-2)]"
      >
        <MoreHorizontal size={16} />
      </Button>
      {open && (
        <MenuSurface
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-44 p-1"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false)
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
              className={cn(
                'block w-full rounded-md px-2.5 py-2 text-left text-sm hover:bg-[var(--fl-surface-hover)]',
                item.danger ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
              )}
            >
              {item.label}
            </button>
          ))}
        </MenuSurface>
      )}
    </div>
  )
}

/** One goal as a Budget-style row: amount of target, a bar with a time-gone tick, and how its pace stands. */
export function GoalRow({
  goal,
  today,
  menu,
  onAdd,
  onWithdraw,
}: {
  goal: GoalResponse
  today: Date
  /** Empty in read-only mode. */
  menu: GoalMenuItem[]
  onAdd?: () => void
  onWithdraw?: () => void
}) {
  const { formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const navigate = useNavigate()
  const href = ROUTES.APP.GOAL_DETAIL(goal.id)
  const isActive = goal.status === 'ACTIVE'
  const remaining = Math.max(fromPaise(toPaise(goal.targetAmount) - toPaise(goal.currentAmount)), 0)
  const badge = isActive ? paceBadge(goal.paceStatus) : null
  const lagging = goal.paceStatus === 'BEHIND' || goal.paceStatus === 'OVERDUE'
  const tick = isActive ? timeGoneFraction(goal.createdAt, goal.targetDate, today) : null

  let note: string
  if (goal.status === 'COMPLETED') {
    note = 'Done'
  } else if (goal.status === 'PAUSED') {
    note = `Paused · ${formatWhole(remaining)} to go`
  } else if (remaining === 0) {
    note = 'Target reached'
  } else if (!wide && goal.neededPerMonth != null && goal.targetDate) {
    note = `${formatWhole(goal.neededPerMonth)}/mo needed · by ${formatMonthYearShort(goal.targetDate)}`
  } else {
    const parts = [
      `${formatWhole(remaining)} to go${goal.targetDate ? ` by ${formatDate(goal.targetDate)}` : ''}`,
    ]
    if (goal.neededPerMonth != null) {
      parts.push(
        `needs ${formatWhole(goal.neededPerMonth)}/mo` +
          (goal.monthlyPace != null ? `, you've averaged ${formatWhole(goal.monthlyPace)}` : '')
      )
    } else if (goal.addedThisMonth) {
      parts.push(
        `${goal.addedThisMonth > 0 ? '+' : '−'}${formatWhole(Math.abs(goal.addedThisMonth))} this month`
      )
    }
    note = parts.join(' · ')
  }

  return (
    <li
      className="cursor-pointer py-3.5"
      onClick={(e) => {
        // Buttons, links and the menu inside the row handle their own clicks.
        if ((e.target as HTMLElement).closest('button, a, [role="menu"]')) return
        navigate(href)
      }}
    >
      <div className="flex items-center gap-2.5 text-sm">
        <CategoryIcon icon={goal.icon || DEFAULT_GOAL_ICON} size={13} className={ICON_TILE} />
        <div className="min-w-0 flex-1">
          <Link
            to={href}
            className="block truncate font-medium text-[var(--fl-text-1)] hover:underline"
          >
            {goal.name}
          </Link>
          <span className="block truncate text-xs text-[var(--fl-text-2)]">
            {wide ? trackedIn(goal) : goal.linkedAccountName}
          </span>
        </div>
        <span
          className={cn(
            'shrink-0 text-right font-semibold tabular-nums',
            goal.status === 'COMPLETED' ? 'text-[var(--fl-income)]' : 'text-[var(--fl-text-1)]'
          )}
        >
          {formatWhole(goal.currentAmount)}
          <span className="font-normal text-[var(--fl-text-2)]">
            {' '}
            / {formatWhole(goal.targetAmount)}
          </span>
        </span>
        {wide && onAdd && (
          <Button
            variant="outline"
            onClick={onAdd}
            className="h-auto shrink-0 gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold text-[var(--fl-primary)]"
          >
            <Plus size={12} /> Add
          </Button>
        )}
        {wide && menu.length > 0 && <RowMenu name={goal.name} items={menu} />}
      </div>
      <div className="relative ml-[38px] mt-2 h-[7px] rounded-full bg-[var(--fl-surface-subtle)]">
        <div
          className={cn(
            'h-full rounded-full',
            lagging ? 'bg-[var(--fl-warning-text)]' : 'bg-[var(--fl-hero-bar)]',
            !isActive && goal.status !== 'COMPLETED' && 'opacity-50'
          )}
          style={{ width: `${Math.min(Number(goal.progressPercent), 100)}%` }}
        />
        {tick != null && (
          <span
            data-testid="time-tick"
            aria-hidden="true"
            className="absolute -top-1 h-[calc(100%+8px)] w-0.5 rounded-full bg-[var(--fl-hero-fg)]"
            style={{ left: `${tick * 100}%` }}
          />
        )}
      </div>
      <div className="ml-[38px] mt-1.5 flex items-center justify-between gap-2 text-xs text-[var(--fl-text-2)]">
        <span className="min-w-0 tabular-nums">{note}</span>
        {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
      </div>
      {goal.earmarkShortfall > 0 && (
        <div className="ml-[38px] mt-1.5 flex items-center justify-between gap-2 text-xs">
          <span className="text-[var(--fl-error)]">
            {goal.linkedAccountName} is {formatWhole(goal.earmarkShortfall)} short of what’s set
            aside
          </span>
          {onWithdraw && (
            <button
              type="button"
              onClick={onWithdraw}
              className="shrink-0 font-semibold text-[var(--fl-error)] underline"
            >
              Withdraw
            </button>
          )}
        </div>
      )}
    </li>
  )
}
